import React, { Fragment, useRef, useState, useEffect } from "react";
import { Carousel, Icon } from "antd";
import { Document, Page } from "react-pdf/dist/esm/entry.webpack";
import {
  DownloadIcon,
  PrintIcon,
  SendAsEmailIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from "./CustomIcon";
import "./DocumentPrviewCarousal.scss";

// Images upload with image_type = "normal" (not "image/...") and the carousel
// names slides by pic_code (e.g. "OTHER ATTACHMENT"), so neither field alone can
// tell image/PDF apart reliably. Sniff the real type from the original File
// object (picture_obj), data-URL MIMEs, or the src/name extension as fallbacks.
const PdfSlide = ({ item, scale }) => {
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState(null);

  if (error) {
    // react-pdf fetches the file over the network and is CORS-gated, so a
    // cross-origin PDF without CORS headers can't be read by it even though an
    // iframe could embed it. Fall back to the original iframe so anything that
    // used to render still renders, and keep the direct "Open" link as well.
    return (
      <Fragment>
        <iframe
          src={item.src}
          title={item.name}
          className="dpc-frame"
          style={{ zoom: scale }}
        />
        <div className="dpc-fallback">
          <a href={item.src} target="_blank" rel="noopener noreferrer">
            Open {item.name}
          </a>
        </div>
      </Fragment>
    );
  }

  return (
    <div className="dpc-pdf">
      <Document
        file={item.src}
        loading={<p className="dpc-pdf-message">Loading PDF...</p>}
        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
        onLoadError={setError}
      >
        {numPages != null &&
          Array.from(new Array(numPages), (_, i) => (
            <Page key={i} pageNumber={i + 1} scale={scale} />
          ))}
      </Document>
    </div>
  );
};

// Fit the image into the viewport at scale=1, then grow a real layout box by
// scale so overflow:auto gets both horizontal and vertical scroll for any
// portrait/landscape size. CSS zoom alone only expanded height reliably here.
const ImageSlide = ({ item, scale }) => {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [baseSize, setBaseSize] = useState(null);

  const measureBaseSize = () => {
    const wrap = wrapRef.current;
    const img = imgRef.current;
    if (!wrap || !img || !img.naturalWidth) return;

    const maxW = wrap.clientWidth;
    const maxH = wrap.clientHeight;
    if (!maxW || !maxH) return;

    const fit = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);
    setBaseSize({
      width: img.naturalWidth * fit,
      height: img.naturalHeight * fit,
    });
  };

  useEffect(() => {
    setBaseSize(null);
    measureBaseSize();
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureBaseSize);
      return () => window.removeEventListener("resize", measureBaseSize);
    }
    const observer = new ResizeObserver(measureBaseSize);
    observer.observe(wrap);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.src]);

  const scaledWidth = baseSize ? baseSize.width * scale : undefined;
  const scaledHeight = baseSize ? baseSize.height * scale : undefined;

  return (
    <div
      className={`dpc-image-wrap${scale > 1 ? " dpc-image-wrap--zoomed" : ""}`}
      ref={wrapRef}
    >
      <div
        className="dpc-image-scaler"
        style={
          baseSize
            ? { width: scaledWidth, height: scaledHeight }
            : undefined
        }
      >
        <img
          ref={imgRef}
          src={item.src}
          alt={item.name}
          className="dpc-image"
          onLoad={measureBaseSize}
          style={
            baseSize
              ? {
                  width: baseSize.width,
                  height: baseSize.height,
                  maxWidth: "none",
                  maxHeight: "none",
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                }
              : undefined
          }
        />
      </div>
    </div>
  );
};

const getPreviewKind = (item) => {
  const type = item.image_type || "";
  const srcUrl = (item.src || "").split("?")[0];
  const name = item.name || "";
  const pictureObj = item.picture_obj;

  if (type.startsWith("image/")) return "image";
  if (type === "application/pdf") return "pdf";
  if (type === "signature" && srcUrl) return "image";

  if (pictureObj && typeof pictureObj.type === "string") {
    if (pictureObj.type.startsWith("image/")) return "image";
    if (pictureObj.type === "application/pdf") return "pdf";
  }

  if (srcUrl.startsWith("data:")) {
    if (/^data:image\//.test(srcUrl)) return "image";
    if (/^data:application\/pdf/.test(srcUrl)) return "pdf";
  }

  const isImageExt = /\.(jpe?g|png|gif|webp|bmp|svg)$/i;
  const pictureName = pictureObj && pictureObj.name;
  if (
    isImageExt.test(name) ||
    isImageExt.test(srcUrl) ||
    (pictureName && isImageExt.test(pictureName))
  ) {
    return "image";
  }
  if (
    /\.pdf$/i.test(name) ||
    /\.pdf$/i.test(srcUrl) ||
    (pictureName && /\.pdf$/i.test(pictureName))
  ) {
    return "pdf";
  }

  return "other";
};

/**
 * Multi-slide preview for a mix of images, PDFs, and other file types.
 *
 * Props:
 * - items: array of { src, name, image_type, ack_id, pictures_id, isSaved }
 * - startIndex: which slide to open on (defaults to 0)
 * - onPrint: callback used for the download/print actions (mirrors FilePreviewer)
 * - setIsPODemailModalVisible: callback to open the "send as email" drawer
 * - toggleSelectDocument: keeps the previewed document in sync with the slide
 * - enableSelection: whether the carousel should update the selected doc
 */
const formatCashReceiptAmount = (val) => {
  if (val === undefined || val === null || val === "") return "N/A";
  const strVal = val.toString().trim();
  const num = parseFloat(strVal.replace(/[^0-9.-]+/g, ""));
  if (isNaN(num)) return strVal;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(num);
};

export default function DocumentPreviewCarousel({
  items = [],
  startIndex = 0,
  onPrint = () => {},
  setIsPODemailModalVisible,
  toggleSelectDocument = () => {},
  enableSelection = true,
  showActions = true,
}) {
  const carouselRef = useRef(null);
  const [current, setCurrent] = useState(startIndex);
  const [scale, setScale] = useState(1);

  // Jump to the right slide whenever the caller opens a different starting item
  useEffect(() => {
    if (carouselRef.current && typeof carouselRef.current.goTo === "function") {
      carouselRef.current.goTo(startIndex, true);
      setCurrent(startIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startIndex]);

  if (items.length === 0) return null;

  const currentItem = items[current] || items[0];

  const goPrev = () => carouselRef.current?.prev();
  const goNext = () => carouselRef.current?.next();

  const handleZoomIn = () =>
    setScale((prevScale) => Math.min(prevScale + 0.1, 2.5));
  const handleZoomOut = () =>
    setScale((prevScale) => Math.max(prevScale - 0.1, 0.5));

  // Keep the "selected/previewed" document in sync so download, email and
  // print always act on the slide currently being viewed.
  const handleSlideChange = (index) => {
    setCurrent(index);
    const item = items[index];
    if (item && enableSelection && item.ack_id) {
      toggleSelectDocument(item.ack_id, item.pictures_id, true);
    }
  };

  const renderActionContainer = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        gap: "8px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "360px",
          height: "56px",
          borderRadius: "64px",
          gap: "24px",
          boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.28)",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "16px",
            padding: "8px",
          }}
        >
          <ZoomOutIcon
            style={{ cursor: "pointer" }}
            onClick={handleZoomOut}
          />
          <ZoomInIcon
            style={{ cursor: "pointer" }}
            onClick={handleZoomIn}
          />
        </div>
        {currentItem?.isSaved && (
          <div
            style={{
              display: "flex",
              gap: "16px",
              padding: "8px",
            }}
          >
            <DownloadIcon
              color="#5064AE"
              style={{ cursor: "pointer" }}
              onClick={() => onPrint(true)}
            />
            <SendAsEmailIcon
              style={{ cursor: "pointer" }}
              onClick={() => {
                setIsPODemailModalVisible &&
                  setIsPODemailModalVisible(true);
              }}
            />
            <PrintIcon
              color="#5064AE"
              style={{ cursor: "pointer" }}
              onClick={() => onPrint()}
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="dpc-wrap">
      {items.length > 1 && (
        <button type="button" className="dpc-arrow dpc-arrow--left" onClick={goPrev} aria-label="Previous">
          <Icon type="left" />
        </button>
      )}

      <Carousel
        ref={carouselRef}
        dots={items.length > 1}
        afterChange={handleSlideChange}
        keyboard={false}
      >
        {items.map((item, index) => {
          const kind = getPreviewKind(item);
          const isCashReceipt =
            (item.pic_code || "").toUpperCase() === "CASH RECEIPT" ||
            (item.pic_title || "").toUpperCase() === "CASH RECEIPT" ||
            (item.name || "").toUpperCase() === "CASH RECEIPT";

          return (
            <div key={item.uid || item.src || index} className="dpc-slide">
              {isCashReceipt && (
                <div
                  className="dpc-cash-receipt-badge"
                  data-testid="cash-receipt-amount-badge"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "10px",
                    background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                    border: "1px solid #a7f3d0",
                    padding: "8px 20px",
                    borderRadius: "24px",
                    margin: "0 auto 16px auto",
                    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.15)",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: "#10b981",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ffffff",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  >
                    $
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span
                      style={{
                        fontSize: "13px",
                        color: "#047857",
                        fontWeight: 600,
                        letterSpacing: "0.2px",
                      }}
                    >
                      Prepaid Amount:
                    </span>
                    <span
                      style={{
                        fontSize: "16px",
                        color: "#065f46",
                        fontWeight: 700,
                      }}
                    >
                      {formatCashReceiptAmount(item.amount)}
                    </span>
                  </div>
                </div>
              )}
              {kind === "image" && (
                <ImageSlide item={item} scale={scale} />
              )}
              {kind === "pdf" && (
                <PdfSlide item={item} scale={scale} />
              )}
              {kind === "other" && (
                <div className="file-preview-error">
                  <p className="file-preview-error-text">
                    Preview not available for this file type.
                  </p>
                  <a
                    className="file-preview-error-button"
                    href={item.src}
                    download
                  >
                    Download {item.name}
                  </a>
                </div>
              )}
              <p className="dpc-caption">{item.name}</p>
            </div>
          );
        })}
      </Carousel>

      {items.length > 1 && (
        <button type="button" className="dpc-arrow dpc-arrow--right" onClick={goNext} aria-label="Next">
          <Icon type="right" />
        </button>
      )}

      {items.length > 1 && (
        <div className="dpc-counter">
          {current + 1} / {items.length}
        </div>
      )}

      {showActions && renderActionContainer()}
    </div>
  );
}
